import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelMonthTabs } from "@/components/fuel-activity/FuelMonthSummaryCards";
import { MonthlySpendBreakdownView } from "@/components/fuel-activity/MonthlySpendBreakdownView";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { getAvailableFuelMonths, getMonthlySpendBreakdown } from "@/lib/fuel-analytics";
import { useFuelActivityData } from "@/lib/queries";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function FuelSpendMonthPage() {
  const { year: yearParam, month: monthParam } = useParams();
  const year = Number(yearParam);
  const month = Number(monthParam) - 1;
  const { data, isLoading, error } = useFuelActivityData();

  const availableMonths = useMemo(
    () => (data ? getAvailableFuelMonths(data.logs, year) : []),
    [data, year]
  );

  const breakdown = useMemo(() => {
    if (!data || !Number.isFinite(year) || !Number.isFinite(month)) return null;
    return getMonthlySpendBreakdown(data.logs, data.users, data.cars, data.stations, year, month);
  }, [data, year, month]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 0 || month > 11) {
    return <Navigate to="/fuel-activity/spend" replace />;
  }

  if (!isLoading && data && availableMonths.length > 0 && !availableMonths.includes(month)) {
    const fallback = availableMonths[availableMonths.length - 1];
    return <Navigate to={`/fuel-activity/spend/${year}/${fallback + 1}`} replace />;
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/fuel-activity"
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          ← User fuel activity
        </Link>
        <Link
          to={`/fuel-activity/spend/${year}`}
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          ← {year} overview
        </Link>
        <Link
          to="/fuel-activity/spend"
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          All years
        </Link>
      </div>

      <PageHeader
        title={breakdown?.label ?? "Monthly spend"}
        description="Fuel spend breakdown for this month across all users."
        showGreeting={false}
      />

      <FuelMenuNav />

      {availableMonths.length > 0 ? (
        <FuelMonthTabs
          months={availableMonths}
          year={year}
          activeMonth={month}
          basePath="/fuel-activity/spend"
        />
      ) : null}

      {isLoading ? (
        <ExplorerSkeleton title="Monthly spend" />
      ) : error || !data || !breakdown ? (
        <p className="text-sm text-red-600">Failed to load monthly spend breakdown.</p>
      ) : (
        <MonthlySpendBreakdownView breakdown={breakdown} scope="platform" />
      )}
    </motion.div>
  );
}
